import * as React from 'react';
import type { ColumnDef } from './types';

/** One column's entry in the Columns visibility menu. */
export interface ColumnMenuItem {
  id: string;
  label: string;
  visible: boolean;
  /** Locked into view — rendered in the menu's "Always visible" section. */
  alwaysVisible: boolean;
}

/** The derived, render-ready view of column visibility for a given hidden set. */
export interface ColumnVisibilityView<T> {
  /** Columns to render, in their original order, with toggled-off columns
   *  removed. Always-visible columns are never removed. */
  visibleColumns: ColumnDef<T>[];
  /** Menu model: every column with its label, current visibility and lock. */
  menuItems: ColumnMenuItem[];
  /** How many columns are currently hidden — drives the trigger badge (0 in the
   *  default all-visible state). */
  hiddenCount: number;
}

/** The columns-independent visibility state — safe to call above an early
 *  return, unlike deriving the view (which needs the columns array). */
export interface ColumnVisibilityState {
  hiddenIds: ReadonlySet<string>;
  /** Toggle a column's visibility. Always-visible columns are re-included by
   *  {@link columnVisibilityView} regardless, so this needs no per-id guard. */
  toggle: (id: string) => void;
  /** Restore the default state — every column visible. */
  reset: () => void;
}

export type ColumnVisibility<T> = ColumnVisibilityView<T> & Pick<ColumnVisibilityState, 'toggle' | 'reset'>;

/** Menu label for a column: explicit `label`, else a string `header`, else the
 *  id as a last resort — with a dev warning, since an id in the menu is almost
 *  always a missing `label` on a column whose header is a ReactNode. */
function columnLabel<T>(col: ColumnDef<T>): string {
  if (col.label) return col.label;
  if (typeof col.header === 'string') return col.header;
  if (import.meta.env.DEV) {
    console.warn(
      `ColumnsMenu: column "${col.id}" has a non-string header and no \`label\`; ` +
        'the menu is falling back to the id. Add a `label` for a readable name.',
    );
  }
  return col.id;
}

/**
 * Column-visibility state with no dependency on the columns array, so it can be
 * called with the rest of a component's hooks — above any early return that
 * builds the columns later (e.g. a `if (!permissions) return null` guard).
 * State is component-local: it resets on unmount / reload, with no persistence.
 */
export function useColumnVisibilityState(): ColumnVisibilityState {
  const [hiddenIds, setHiddenIds] = React.useState<ReadonlySet<string>>(() => new Set());

  const toggle = React.useCallback((id: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const reset = React.useCallback(() => setHiddenIds(new Set()), []);

  return { hiddenIds, toggle, reset };
}

/** Derive the render-ready visibility view from columns + a hidden set. Pure —
 *  no hooks — so it can run below an early return. Always-visible columns are
 *  never hidden even if their id is in `hiddenIds`. */
export function columnVisibilityView<T>(
  columns: ColumnDef<T>[],
  hiddenIds: ReadonlySet<string>,
): ColumnVisibilityView<T> {
  const isHidden = (col: ColumnDef<T>): boolean => !col.alwaysVisible && hiddenIds.has(col.id);
  const visibleColumns = columns.filter((col) => !isHidden(col));
  const menuItems: ColumnMenuItem[] = columns.map((col) => ({
    id: col.id,
    label: columnLabel(col),
    visible: !isHidden(col),
    alwaysVisible: !!col.alwaysVisible,
  }));
  return { visibleColumns, menuItems, hiddenCount: columns.length - visibleColumns.length };
}

/**
 * Convenience hook combining {@link useColumnVisibilityState} and
 * {@link columnVisibilityView} for the common case where the columns array is
 * available above every early return.
 */
export function useColumnVisibility<T>(columns: ColumnDef<T>[]): ColumnVisibility<T> {
  const { hiddenIds, toggle, reset } = useColumnVisibilityState();
  const view = React.useMemo(() => columnVisibilityView(columns, hiddenIds), [columns, hiddenIds]);
  return { ...view, toggle, reset };
}
